/**
 * @file    useChartTypeChangeEffect.ts
 * @scope   SHARED
 * @summary Reacts to a chart-type change: rebuilds the widget draft from the widget catalogue,
 *          resets the selections that no longer apply and re-fetches the chart config.
 * @consumes The resolvers/handlers injected by DiyDrawer (all pure adapters over `common/utils`)
 * @produces Nothing — it publishes the rebuilt widget through `setState`.
 * @sideEffects Writes `customWidget` to the global store, clears selections/trend and fetches
 *              chart data.
 * @notes The effect body is split into four single-responsibility steps (resolve → rebuild →
 *        reset → fetch). Behaviour, ordering and the `[currentAlias]` dependency array are pinned
 *        by ./useChartTypeChangeEffect.test.ts — read those characterization tests (including the
 *        `// QUIRK:` ones) before changing anything here.
 */
import { useEffect } from 'react';
import { initWidget, WidgetAlias } from 'models/WidgetModel';
import { DIYChartOption } from 'constants/chart-selector';
import { getChartTypeData } from 'services/Torogoz/TogorozApi';
import { buildPreviewDataSource } from '../utils/dataSourceResolver';
import { buildChartTypeWidgetDraft } from '../utils/widgetDraftBuilder';

/** Data source value marking a widget that has no Guardrails counterpart. */
const GENERIC_DATA_SOURCE = 'generic';

/** Alias of a widget that has not been given a chart type yet. */
const BRAND_NEW_WIDGET_ALIAS = 'CustomWidget';

/** Inputs of {@link useChartTypeChangeEffect}. */
export type UseChartTypeChangeEffectParams = {
  /** Chart option currently selected; the effect's only trigger. */
  currentAlias: DIYChartOption | undefined;
  /** Widget draft being edited. */
  customWidget: any;
  /** Dropdown options; the effect is a no-op until the catalog has arrived. */
  chartTypes: DIYChartOption[] | undefined;
  /** Widget catalogue used to resolve the component backing the new chart type. */
  widgetsList: any[];
  /** Child-chart selection (drives the `isChild` metadata). */
  selectedDiyChildChart: any;
  /** ENTERPRISE-ONLY: partition currently focused, used to build the preview data source. */
  selectedPartition: any;
  /** Rule set returned by Guardrails for the current chart. */
  responseRules: any[];
  /** ENTERPRISE-ONLY: `true` when the selected chart belongs to the Enterprise family. */
  isDiyWidgetEngine: boolean;
  /** Consumer-mode flag forwarded to the API layer. */
  isConsumer: boolean;
  /** Secondary axios channel fetcher. */
  axiosFetchData: any;
  /** Global store writer. */
  setState: (partial: any) => void;
  /** Resets the "user touched questions/calculations" flag. */
  setTouchedQC: (touched: boolean) => void;
  /** Clears the legacy question/calculation selection. */
  clearSelections: () => void;
  /** Resets the trend selection to its minimum. */
  resetTrendToMinimum: () => void;
  /** ENTERPRISE-ONLY: clears the Enterprise builder selection when leaving the Enterprise family. */
  clearEnterpriseSelectedValues: () => void;
  /** Re-applies the rule filter for the current chart option. */
  filterRules: (rules: any[]) => void;
  /** `true` when the selected option no longer matches the widget alias. */
  hasAliasChanged: (chartType: string, alias: string) => boolean;
  /** Resolves the preview data source (and publishes the child-chart rule name). */
  getDataSourceForPreview: (newAlias: string, trend?: any) => any;
  /** Builds the Guardrails getData URL for an option. */
  getDataUrlEndpoint: (alias?: any) => string;
  /** Builds the default options of the rebuilt widget. */
  buildOptions: (selectedWidget: any, currentWidget: any) => any;
  /** Resolves the data-source type of a chart alias. */
  resolveSourceType: (alias: string) => string;
  /** Resolves the color palette of a chart alias. */
  colorByChartType: (alias: string) => any;
};

export const useChartTypeChangeEffect = ({
  currentAlias,
  customWidget,
  chartTypes,
  widgetsList,
  selectedDiyChildChart,
  selectedPartition,
  responseRules,
  isDiyWidgetEngine,
  isConsumer,
  axiosFetchData,
  setState,
  setTouchedQC,
  clearSelections,
  resetTrendToMinimum,
  clearEnterpriseSelectedValues,
  filterRules,
  hasAliasChanged,
  getDataSourceForPreview,
  getDataUrlEndpoint,
  buildOptions,
  resolveSourceType,
  colorByChartType,
}: UseChartTypeChangeEffectParams): void => {
  useEffect(() => {
    if (!currentAlias) return;

    /** Fetches the Guardrails config of the option currently selected. */
    const fetchChartData = async () => {
      await getChartTypeData(getDataUrlEndpoint(currentAlias), axiosFetchData, isConsumer);
    };

    /** `true` once the catalog has arrived; nothing can be resolved before that. */
    const catalogIsReady = Boolean(chartTypes?.length);

    /** The user picked a chart type that differs from the one the draft currently holds. */
    const chartTypeChanged = Boolean(
      currentAlias.chartType && hasAliasChanged(currentAlias.chartType, customWidget.alias)
    );

    /**
     * STEP 1 — Rebuild the draft from the widget catalogue.
     *
     * @returns `true` when the draft was actually republished, which suppresses the refresh step.
     */
    const rebuildDraftForNewChartType = (): boolean => {
      const newAlias = currentAlias.component || customWidget.alias;
      const catalogueWidget = widgetsList.find((widget) => widget.component === newAlias);
      const dataSource = getDataSourceForPreview(newAlias);
      const previewDataSource = buildPreviewDataSource(dataSource, currentAlias, selectedPartition);

      filterRules(responseRules || []);

      // The catalogue may not expose a widget for this chart type; the refresh step takes over.
      if (!catalogueWidget) return false;

      const [catalogueAlias] = catalogueWidget.alias.filter(
        (item: WidgetAlias) => item.controllerName === currentAlias.chartType
      );
      const initializedWidget = initWidget(catalogueWidget, catalogueAlias, customWidget.config);

      setTouchedQC(false);

      setState({
        customWidget: buildChartTypeWidgetDraft({
          initializedWidget,
          customWidget,
          previewDataSource,
          dataSource: resolveSourceType(currentAlias.chartType as string),
          isChild: Boolean(selectedDiyChildChart?.isChild),
          options: buildOptions(initializedWidget, customWidget),
          colors: colorByChartType(newAlias),
        }),
      });

      return true;
    };

    /** STEP 2 — Drop the selections that cannot survive a chart-type switch. */
    const resetSelectionsForNewChartType = () => {
      clearSelections();
      resetTrendToMinimum();
      // ENTERPRISE-ONLY: leaving the Enterprise family must not keep its builder selection around.
      if (!isDiyWidgetEngine) clearEnterpriseSelectedValues();
    };

    /**
     * STEP 3 — Refresh the config of the widget already in the draft.
     * WHY: reached when nothing was rebuilt, so the drawer still needs the chart's Guardrails
     * config — unless the widget is brand-new or generic (client-side), which has none.
     */
    const refreshCurrentWidgetConfig = () => {
      if (customWidget.alias === BRAND_NEW_WIDGET_ALIAS || !catalogIsReady) return;

      filterRules(responseRules || []);

      const persistedSourceIsGeneric =
        customWidget?.customWidgetConfig?.dataSource === GENERIC_DATA_SOURCE;
      const resolvedSourceIsGeneric = resolveSourceType(customWidget.alias) === GENERIC_DATA_SOURCE;

      if (!persistedSourceIsGeneric && !resolvedSourceIsGeneric) fetchChartData();
    };

    // STEP 4 — Sequence the steps. Order is behaviour: the reset and the fetch only happen for a
    // draft that was actually rebuilt.
    const draftWasRebuilt =
      chartTypeChanged && catalogIsReady ? rebuildDraftForNewChartType() : false;

    if (draftWasRebuilt) {
      resetSelectionsForNewChartType();
      if (resolveSourceType(currentAlias.chartType as string) !== GENERIC_DATA_SOURCE)
        fetchChartData();
      return;
    }

    refreshCurrentWidgetConfig();
  }, [currentAlias]);
};
